/**
 * Ensures a search record exists in the backend. Creates one only if company_store.searchID is empty.
 * Resolves UTMs directly (URL/cookie/session) to avoid Redux timing gaps; backfills existing searches.
 */
import axios from '../../shared/axios/axiosProxy';
import {
	dispatchUtmParams,
	hasAnyUtm,
	mergeUtmIntoPayload,
	resolveUtmParams,
} from './utmBootstrap';
import ACTIONS from '../../redux/action';

const FLOW_CONFIG = {
	ASP: { leadtype: 'ASP', flowStart: 'ASP' },
	VAF: { leadtype: 'VAF', flowStart: 'VAF' },
	REG: { leadtype: 'REG', flowStart: 'REG' },
	KFTREG: { leadtype: 'KFTREG', flowStart: 'KFTREG' },
};

let pendingCreate = null;

function syncUtmToRedux(addCompanyInfo, utm) {
	if (!addCompanyInfo || !hasAnyUtm(utm)) return;
	for (const [id, value] of Object.entries(utm)) {
		if (value) {
			addCompanyInfo({ id, data: value, stateObject: 'company_store' });
		}
	}
	if (utm.utm_medium === 'referral' && utm.utm_campaign) {
		addCompanyInfo({
			id: 'discountCode',
			data: utm.utm_campaign,
			stateObject: 'company_store',
		});
	}
}

async function backfillSearchUtms(searchID, utm) {
	if (!searchID || !hasAnyUtm(utm)) return;
	const payload = {};
	for (const [key, value] of Object.entries(utm)) {
		if (value) payload[key] = value;
	}
	try {
		await axios.patch(`/api/search/${searchID}`, payload, {
			headers: { 'Content-Type': 'application/json' },
		});
	} catch (error) {
		console.warn(
			'[ensureSearchExists] UTM backfill failed:',
			error?.response?.data || error.message
		);
	}
}

export async function ensureSearchExists({
	flowType,
	abVersion,
	company_store,
	addCompanyInfo,
	dispatch,
	step = 1,
	stepData,
}) {
	const utm = resolveUtmParams({ company_store });

	if (addCompanyInfo) {
		syncUtmToRedux(addCompanyInfo, utm);
	} else if (dispatch) {
		dispatchUtmParams(dispatch, ACTIONS, utm);
	}

	// Falls back to a `?searchID=` URL param so a hard navigation (full page load)
	// between flows — e.g. searchFlowVersionB's CommonRouting "founding" handoff to
	// the plain search flow's own CompanyType step — can still resume the same
	// search record, since company_store itself does not survive a full reload.
	const urlSearchID = new URLSearchParams(window.location.search).get('searchID');
	const existingId = company_store.searchID || urlSearchID;
	if (existingId && existingId !== '' && existingId !== null) {
		if (!company_store.searchID && urlSearchID && addCompanyInfo) {
			addCompanyInfo({ id: 'searchID', data: existingId, stateObject: 'company_store' });
		}
		await backfillSearchUtms(existingId, utm);
		return existingId;
	}
	if (pendingCreate) {
		return pendingCreate;
	}

	const config = FLOW_CONFIG[flowType] || FLOW_CONFIG.ASP;
	const normalizedAbVersion = abVersion || 'A';
	const data = {
		...company_store,
		step,
		returnedProviders: 0,
		screenWidth: `${window.innerWidth}`,
		lang: company_store.chosenLanguage || 'hu',
		leadtype: config.leadtype,
		// A brand-new search's flowStart is, by definition, the flow creating it right
		// now — company_store.flowStart can't hold a meaningful prior value here (if it
		// did, company_store.searchID would already be set and we'd have hit the reuse
		// branch above instead). It defaults to 'ASP' in redux's initial state, so
		// falling back to it here silently mislabeled every fresh VAF/REG/KFTREG search
		// as flowStart: 'ASP'.
		flowStart: config.flowStart,
		abVersion: normalizedAbVersion,
		sourceUrl: company_store.sourceUrl || '',
	};
	if (normalizedAbVersion !== 'B') {
		data.time =
			stepData ||
			`1 - ${new Date().toLocaleTimeString('hu-HU', { hour12: false })}`;
	}
	mergeUtmIntoPayload(data, utm, company_store);

	pendingCreate = (async () => {
		try {
			const response = await axios.post(`/api/search`, data, {
				headers: { 'Content-Type': 'application/json' },
			});
			const searchID = response.data?.result?._id;
			if (searchID) {
				addCompanyInfo?.({
					id: 'searchID',
					data: searchID,
					stateObject: 'company_store',
				});
				addCompanyInfo?.({
					id: 'flowStart',
					data: config.flowStart,
					stateObject: 'company_store',
				});
				return searchID;
			}
		} catch (error) {
			console.error(
				'[ensureSearchExists] Error creating search:',
				error?.response?.data || error.message
			);
		} finally {
			pendingCreate = null;
		}
		return null;
	})();

	return pendingCreate;
}
